import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../common/Layout';
import PositionsHeader from './ledger/PositionsHeader';
import PositionLedger from './ledger/PositionLedger';
import PortfolioTabNavigation from './PortfolioTabNavigation';
import ResolveModal from './ResolveModal';
import RollTicket from './ledger/RollTicket';
import { RollOption } from '../../services/api';
import PortfolioBriefModal from './PortfolioBriefModal';
import { usePortfolioKPIs } from '../../contexts/PortfolioKPIContext';
import { useLatestPositionPnl } from '../../hooks/useLatestPrices';
import { LatestPositionQuote } from '../../types/latestPrice';
import { apiService } from '../../services/api';
import { LedgerPosition } from '../../hooks/usePortfolioKPILoader';
import AssignedSharesPanel from './ledger/AssignedSharesPanel';
import { DisciplineState, Intent } from '../../types/discipline';
import { urgencyTier, countVerdicts, verdictTone } from '../../utils/disciplineVerdict';
import { LedgerFilter } from '../../utils/positionLedger';

type CoveredCallPosition = LedgerPosition;

const PortfolioPositions: React.FC = () => {
  const navigate = useNavigate();
  const { kpis, refreshKPIs } = usePortfolioKPIs();

  // Only show active positions (closed positions are never needed)
  const allPositions = useMemo(() => {
    return [...(kpis.activePositions || [])];
  }, [kpis.activePositions]);

  // Use KPI loading state
  const loading = kpis.loading;

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [toneFilter, setToneFilter] = useState<LedgerFilter>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [chartDataCache, setChartDataCache] = useState<Record<string, { results: any[] }>>({});

  // BAB-67: page-level fetch of the user's discipline thresholds bundle.
  // Single GET on mount, then handed down to every card so each can compute
  // its decision-map overlays locally without a second roundtrip. The bundle
  // changes rarely (only when the user saves in Settings) so a manual refetch
  // hook isn't worth the complexity in V1 — page reload covers it.
  const [thresholdsBundle, setThresholdsBundle] =
    useState<import('../../types/discipline').DisciplineThresholdsBundle | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiService.getDisciplineThresholds()
      .then((b) => { if (!cancelled) setThresholdsBundle(b); })
      .catch(() => { if (!cancelled) setThresholdsBundle(null); });
    return () => { cancelled = true; };
  }, []);

  // Oct 2026: page-level Discipline verdicts (one GET per position, in
  // parallel, like the P&L bundles above). The page needs them to sort the grid
  // by urgency and to count verdicts for the summary line; cards receive theirs
  // as a prop. Keyed on the position ids so price ticks don't refetch.
  //   missing key → undefined (loading) · null → fetch failed · object → verdict
  const positionIdsKey = useMemo(() => allPositions.map((p) => p.id).sort().join(','), [allPositions]);
  const [disciplines, setDisciplines] = useState<Map<string, DisciplineState | null>>(new Map());
  useEffect(() => {
    const ids = positionIdsKey ? positionIdsKey.split(',') : [];
    if (ids.length === 0) {
      setDisciplines(new Map());
      return;
    }
    let cancelled = false;
    Promise.all(
      ids.map(async (id): Promise<[string, DisciplineState | null]> => {
        try {
          return [id, await apiService.getPositionDiscipline(id)];
        } catch {
          return [id, null];
        }
      })
    ).then((entries) => {
      if (!cancelled) setDisciplines(new Map(entries));
    });
    return () => { cancelled = true; };
  }, [positionIdsKey]);

  // Intent / thesis edits made in the Manage modal: applied right away, until
  // the next portfolio refresh brings the saved values back.
  const [ruleOverrides, setRuleOverrides] = useState<Map<string, { intent?: Intent; thesisBroken?: boolean }>>(new Map());
  const setRuleOverride = (id: string, patch: { intent?: Intent; thesisBroken?: boolean }) =>
    setRuleOverrides((prev) => {
      const next = new Map(prev);
      next.set(id, { ...(prev.get(id) ?? {}), ...patch });
      return next;
    });
  const updateDiscipline = (id: string, d: DisciplineState) => {
    setDisciplines((prev) => new Map(prev).set(id, d));
    setResolveDiscipline((cur) => (resolvePosition?.id === id ? d : cur));
  };

  // Manage-modal state. BAB-77: the card hands up its live DisciplineState
  // with the 'resolve' action so the modal renders the SAME verdict object
  // the banner shows — single source, no refetch, no second engine.
  const [showResolveModal, setShowResolveModal] = useState(false);
  // Roll order ticket + its Undo toast (the roll is reversible for 15 min).
  const [rollTicketPos, setRollTicketPos] = useState<CoveredCallPosition | null>(null);
  const [rollToast, setRollToast] = useState<{ text: string; legId: string | null; undone?: boolean } | null>(null);
  useEffect(() => {
    if (!rollToast) return;
    const t = setTimeout(() => setRollToast(null), rollToast.undone ? 4000 : 20000);
    return () => clearTimeout(t);
  }, [rollToast]);
  const [resolvePosition, setResolvePosition] = useState<CoveredCallPosition | null>(null);
  const [resolveDiscipline, setResolveDiscipline] = useState<DisciplineState | null>(null);

  // Portfolio brief state
  const [showBriefModal, setShowBriefModal] = useState(false);
  const [briefText, setBriefText] = useState<string | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);
  const [briefCached, setBriefCached] = useState(false);

  // Fetch batch chart data when positions change
  useEffect(() => {
    const fetchBatchChartData = async () => {
      if (allPositions.length === 0) return;

      const symbols = allPositions.map(p => p.ticker);

      try {
        // Calculate date range: 3 months, daily data
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(endDate.getMonth() - 3);

        const fromDate = startDate.toISOString().split('T')[0];
        const toDate = endDate.toISOString().split('T')[0];

        const response = await apiService.getStockDataBatch(
          symbols,
          'day', // Daily timespan
          fromDate,
          toDate
        );

        if (response.status === 'success') {
          setChartDataCache(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch batch chart data:', error);
      }
    };

    fetchBatchChartData();
  }, [allPositions]);

  // Filter positions (active only + search)
  const filteredPositions = useMemo(() => {
    let filtered = allPositions.filter(p => p.status !== 'closed');

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.ticker.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allPositions, searchQuery]);

  // Latest P&L on the latest delayed price (glossary stock_price_live):
  // computed by the server with the same definitions as the 15-min P&L and
  // shown next to it, never instead of it. Rows and header keep the backend's
  // 15-min fields, so the header is the sum of the rows.
  const latestPnl = useLatestPositionPnl(allPositions.length > 0, positionIdsKey);
  const latestByPosition = useMemo(() => {
    const m = new Map<string, LatestPositionQuote>();
    (latestPnl?.positions ?? []).forEach((q) => m.set(q.position_id, q));
    return m;
  }, [latestPnl]);

  // Urgency order: protective stops, then other fired rules, then quiet; inside
  // a tier, fewest days to expiry first. Stable on ties (keeps arrival order).
  const sortedPositions = useMemo(() => {
    return filteredPositions
      .map((p, i) => ({ p, i }))
      .sort((a, b) => {
        const ta = urgencyTier(disciplines.get(a.p.id));
        const tb = urgencyTier(disciplines.get(b.p.id));
        if (ta !== tb) return ta - tb;
        const da = a.p.daysToExpiration ?? 999;
        const db = b.p.daysToExpiration ?? 999;
        return da !== db ? da - db : a.i - b.i;
      })
      .map(({ p }) => {
        const o = ruleOverrides.get(p.id);
        return o ? { ...p, ...(o.intent ? { intent: o.intent } : {}), ...(o.thesisBroken !== undefined ? { thesisBroken: o.thesisBroken } : {}) } : p;
      });
  }, [filteredPositions, disciplines, ruleOverrides]);

  const verdictCounts = useMemo(
    () => countVerdicts(filteredPositions.map((p) => disciplines.get(p.id))),
    [filteredPositions, disciplines]
  );
  const verdictsLoaded = filteredPositions.some((p) => disciplines.has(p.id));
  const visiblePositions = useMemo(
    () => (toneFilter === 'all' ? sortedPositions : sortedPositions.filter((p) => {
      const d = disciplines.get(p.id);
      return d ? verdictTone(d) === toneFilter : false;
    })),
    [sortedPositions, disciplines, toneFilter]
  );

  // Handle position action — BAB-78: the Manage modal is the ONE routed
  // surface (read → decide → act); there is no second action modal to hop to.
  const handlePositionAction = (position: CoveredCallPosition, action: string, discipline?: DisciplineState | null) => {
    // A Roll verdict goes straight to the roll order ticket; 'manage' (the
    // ticket's "All actions…") always opens the full dialog.
    if (action === 'resolve' && discipline?.action === 'Roll') {
      setRollTicketPos(position);
      return;
    }
    if (action !== 'resolve' && action !== 'manage') return;
    setResolvePosition(position);
    setResolveDiscipline(discipline ?? null);
    setShowResolveModal(true);
  };

  // BAB-78: commit a close from the flow modal's confirmation step. Errors
  // propagate to the modal's inline error state; the modal stays open to show
  // the share artifact. closePremium is required by the backend (422 without
  // it, phase 2a): there is no server-side "live" re-mark any more.
  // Close: the backend records the fill the user confirmed in the dialog
  // (prefilled with the last trade) and the Price for the stock leg; the
  // close date is the New York date of the request (gold v2 phase 3b).
  const handleConfirmClose = async (
    payload: { fillPrice: number; usedManualFill: boolean; notes?: string }
  ) => {
    if (!resolvePosition) return;
    await apiService.recordPositionAction(
      resolvePosition.id,
      'close',
      payload.notes,
      {
        closePremium: payload.fillPrice,
        closePrice: resolvePosition.currentPrice ?? undefined,
      }
    );
    if (refreshKPIs) {
      await refreshKPIs();
    }
  };

  // Handle position deletion
  const handlePositionDelete = async (positionId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await apiService.deletePortfolioPosition(positionId);
        // Refresh KPIs (which includes positions in context)
        if (refreshKPIs) {
          await refreshKPIs();
        }
      }
    } catch (error) {
      console.error('Failed to delete position:', error);
      alert('Failed to delete position. Please try again.');
    }
  };

  // Roll order ticket: execute the chosen contract, then offer Undo.
  const handleConfirmRollTicket = async (o: RollOption, ctx: { buyback: number; sell: number; stock: number }) => {
    const pos = rollTicketPos;
    if (!pos) return null;
    const put = pos.positionType === 'cash_secured_put';
    const k = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0$/, ''));
    const code = (iso: string, v: number) => `${iso.slice(5, 7)}/${iso.slice(8, 10)} ${k(v)}${put ? 'P' : 'C'}`;
    const res = await apiService.rollPosition(pos.id, {
      buybackPremium: ctx.buyback,
      newExpiryDate: o.expiry,
      newStrikePrice: o.strike,
      newPremium: ctx.sell,
      currentStockPrice: ctx.stock,
      notes: `Rolled to ${code(o.expiry, o.strike)}: bought back at ${ctx.buyback.toFixed(2)}, sold at ${ctx.sell.toFixed(2)}`,
    });
    // The API returns raw JSON (snake_case); the TS type predates it.
    const newLegId: string | null = (res as any)?.position_id ?? (res as any)?.positionId ?? null;
    setRollTicketPos(null);
    setRollToast({
      text: `Rolled ${pos.ticker} ${code(pos.expiryDate, pos.strikePrice)} → ${code(o.expiry, o.strike)}`,
      legId: newLegId,
    });
    if (refreshKPIs) await refreshKPIs();
    return newLegId;
  };

  const handleUndoRoll = async () => {
    if (!rollToast?.legId) return;
    try {
      await apiService.undoRoll(rollToast.legId);
      setRollToast({ text: 'Roll undone. The previous contract is back.', legId: null, undone: true });
      if (refreshKPIs) await refreshKPIs();
    } catch (e: any) {
      setRollToast({ text: e?.response?.data?.detail || "Couldn't undo the roll.", legId: null, undone: true });
    }
  };

  // Handle portfolio brief (streaming)
  const handleFetchBrief = async (forceRefresh: boolean = false) => {
    setBriefLoading(true);
    setBriefError(null);
    if (forceRefresh) setBriefText(null);
    setShowBriefModal(true);
    try {
      let accumulated = '';
      await apiService.streamPortfolioBrief(
        forceRefresh,
        (chunk) => {
          accumulated += chunk;
          setBriefText(accumulated);
        },
        (cached) => {
          setBriefCached(cached);
          setBriefLoading(false);
        },
        (error) => {
          setBriefError(error || 'Failed to generate portfolio brief. Please try again.');
          setBriefLoading(false);
        },
      );
    } catch (error: any) {
      setBriefError('Failed to generate portfolio brief. Please try again.');
      setBriefLoading(false);
    }
  };

  const handleRefreshBrief = () => {
    handleFetchBrief(true);
  };

  // Handle navigation to performance page
  const handleNavigateToPerformance = () => {
    navigate('/portfolio/portfolio');
  };

  return (
    <Layout>
      <div className="app-page-container">
        {/* Tab Navigation */}
        <PortfolioTabNavigation />

        {/* Header: KPIs + freshness, then filters / search / brief (replaces the KPI strip, search row and summary bar) */}
        <PositionsHeader
          total={filteredPositions.length}
          latest={latestPnl}
          counts={verdictsLoaded ? verdictCounts : null}
          filter={toneFilter}
          setFilter={setToneFilter}
          search={searchQuery}
          setSearch={setSearchQuery}
          onBrief={() => handleFetchBrief()}
          briefLoading={briefLoading}
        />

        <AssignedSharesPanel shares={kpis.assignedShares} onAnswered={() => refreshKPIs && refreshKPIs()} />

        {/* Portfolio Brief Modal */}
        <PortfolioBriefModal
          isOpen={showBriefModal}
          onClose={() => setShowBriefModal(false)}
          onRefresh={handleRefreshBrief}
          isLoading={briefLoading}
          brief={briefText}
          error={briefError}
          cached={briefCached}
          kpis={kpis}
        />

        {/* Positions List */}
        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 40px',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #374151',
              borderTopColor: '#2962ff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{
              color: '#9CA3AF',
              fontSize: '14px'
            }}>
              Loading positions...
            </span>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : filteredPositions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#9CA3AF',
            fontSize: '14px'
          }}>
            {searchQuery ? 'No positions found matching your search.' : 'No positions yet. Add your first covered call position to get started.'}
          </div>
        ) : (
          <PositionLedger
            positions={visiblePositions}
            closedToday={kpis.closedTodayPositions as LedgerPosition[] | undefined}
            disciplines={disciplines}
            latestPnl={latestByPosition}
            thresholdsBundle={thresholdsBundle}
            chartDataCache={chartDataCache}
            openId={openId}
            setOpenId={setOpenId}
            onAction={handlePositionAction}
            onDelete={handlePositionDelete}
            onIntentChanged={(id, intent, d) => {
              setRuleOverride(id, { intent });
              if (d) updateDiscipline(id, d);
            }}
          />
        )}

        {/* Manage modal — BAB-78: the ONE routed surface (read → decide →
            act). Renders the card's DisciplineState (single verdict source),
            then routes Close to the in-place confirmation (+ share artifact)
            and Roll to the roll order ticket (the one roll UI). */}
        {resolvePosition && (
          <ResolveModal
            isOpen={showResolveModal}
            position={resolvePosition}
            discipline={resolveDiscipline}
            onClose={() => {
              setShowResolveModal(false);
              setResolvePosition(null);
              setResolveDiscipline(null);
            }}
            onConfirmClose={handleConfirmClose}
            onOpenRoll={() => {
              setShowResolveModal(false);
              setRollTicketPos(resolvePosition);
            }}
            intent={
              ruleOverrides.get(resolvePosition.id)?.intent
                ?? resolvePosition.intent
                ?? (resolvePosition.keepSharesPreference === false ? 'release' : 'hold')
            }
            thesisBroken={ruleOverrides.get(resolvePosition.id)?.thesisBroken ?? resolvePosition.thesisBroken ?? false}
            onIntentChange={(next) => setRuleOverride(resolvePosition.id, { intent: next })}
            onThesisBrokenChange={(next) => setRuleOverride(resolvePosition.id, { thesisBroken: next })}
            onDisciplineChange={(d) => updateDiscipline(resolvePosition.id, d)}
          />
        )}
      </div>
      {rollTicketPos && (
        <RollTicket
          position={rollTicketPos}
          onClose={() => setRollTicketPos(null)}
          onConfirm={handleConfirmRollTicket}
        />
      )}
      {rollToast && (
        <div className="rt-toast" role="status">
          <span>{rollToast.text}</span>
          {rollToast.legId && <button type="button" onClick={handleUndoRoll}>Undo</button>}
          <button type="button" className="rt-toast-x" aria-label="Dismiss" onClick={() => setRollToast(null)}>×</button>
        </div>
      )}
    </Layout>
  );
};

export default PortfolioPositions;
